import { pick, type Rng } from "../rng";
import { sameQuestion, TIERS, type Question, type Tier } from "./question";
import { makeQuestion, type QuestionType } from "./types";

/** Share of questions from each tier, as percentages. Missing tiers are 0. */
export type TierMix = { [T in Tier]?: number | undefined };

export interface SampleOptions {
  /** The types to draw from, already narrowed to the strands in play. */
  types: readonly QuestionType[];
  mix: TierMix;
  rng: Rng;
  previous: Question | null;
  /** Relative weight of each type within its tier and strand (Practice only). */
  weight?: (type: QuestionType) => number;
}

export function pickWeighted<T>(rng: Rng, items: readonly T[], weight: (item: T) => number): T {
  const weights = items.map((x) => Math.max(0, weight(x)));
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return pick(rng, items);
  let r = rng.next() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i]!;
    if (r < 0) return items[i]!;
  }
  return items[items.length - 1]!;
}

/** Tiers the mix asks for that the pool can serve; if none, every tier the pool has. */
export function tiersInPlay(types: readonly QuestionType[], mix: TierMix): Tier[] {
  const has = (t: Tier) => types.some((q) => q.tier === t);
  const wanted = TIERS.filter((t) => (mix[t] ?? 0) > 0 && has(t));
  return wanted.length ? wanted : TIERS.filter(has);
}

/**
 * One question: a tier by the mix percentages, then a strand evenly among
 * those with types at that tier (so Mix isn't skewed towards strands with more
 * types), then a type (evenly, or by `weight`). Avoids an exact repeat of the
 * previous question, up to 10 tries.
 */
export function sampleQuestion({ types, mix, rng, previous, weight }: SampleOptions): Question {
  if (types.length === 0) throw new Error("no question types to sample from");
  const tiers = tiersInPlay(types, mix);
  let q: Question;
  let tries = 0;
  do {
    const tier = pickWeighted(rng, tiers, (t) => mix[t] ?? 1);
    const atTier = types.filter((t) => t.tier === tier);
    const op = pick(rng, [...new Set(atTier.map((t) => t.op))]);
    const choices = atTier.filter((t) => t.op === op);
    const type = weight ? pickWeighted(rng, choices, weight) : pick(rng, choices);
    q = makeQuestion(type, rng);
    tries++;
  } while (sameQuestion(previous, q) && tries < 10);
  return q;
}
